import React from "react";
import { Calendar } from "lucide-react";

import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

// ----------------------------------------------------------------------------

interface DateRangeFilterProps {
  startDate: string;
  endDate: string;
  onStartDateChange: (value: string) => void;
  onEndDateChange: (value: string) => void;
  className?: string;
}

export function DateRangeFilter(props: DateRangeFilterProps) {
  // -------------------------------------
  // Props destructuring
  // -------------------------------------

  const { startDate, endDate, onStartDateChange, onEndDateChange, className } =
    props;

  // -------------------------------------
  // Hooks (e.g. useState, useMemo ...)
  // -------------------------------------

  const startDateRef = React.useRef<HTMLInputElement | null>(null);
  const endDateRef = React.useRef<HTMLInputElement | null>(null);

  // -------------------------------------
  // Effects
  // -------------------------------------

  // -------------------------------------
  // Component functions
  // -------------------------------------

  function openPicker(input: HTMLInputElement | null) {
    try {
      input?.showPicker?.();
    } catch (error) {
      console.warn(
        "[DateRangeFilter] Unable to open native date picker:",
        error,
      );
    }
  }

  // -------------------------------------
  // Component local variables
  // -------------------------------------

  return (
    <div
      className={cn(
        "flex w-full flex-row justify-between gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 shadow-sm sm:w-auto sm:flex-row sm:items-center",
        className,
      )}
    >
      <div className="flex items-center gap-2 text-gray-500">
        <Calendar className="h-4 w-4 shrink-0" />
      </div>
      <div className="flex items-center gap-2 min-w-0">
        <div
          className="min-w-0"
          onClick={() => openPicker(startDateRef.current)}
        >
          <Input
            ref={startDateRef}
            type="date"
            value={startDate}
            onChange={(event) => onStartDateChange(event.target.value)}
            className="h-8 w-max min-w-0 cursor-pointer border-0 bg-transparent px-0 pr-1 text-sm shadow-none focus-visible:ring-0"
            aria-label="Start date"
          />
        </div>
        <span className="text-sm text-gray-400">-</span>
        <div className="min-w-0" onClick={() => openPicker(endDateRef.current)}>
          <Input
            ref={endDateRef}
            type="date"
            value={endDate}
            onChange={(event) => onEndDateChange(event.target.value)}
            className="h-8 w-max min-w-0 cursor-pointer border-0 bg-transparent px-0 pr-1 text-sm shadow-none focus-visible:ring-0"
            aria-label="End date"
          />
        </div>
      </div>
    </div>
  );
}
